import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Alert,
  Button,
  Center,
  Container,
  Divider,
  Group,
  Loader,
  Stack,
} from "@mantine/core";
import { IconArrowLeft, IconAlertCircle } from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import PageContent from "../shared/PageContent/PageContent";
import PinGate from "../shared/PinGate/PinGate";
import {
  isPinRequiredResponse,
  isPinUnlockSessionActive,
  pinUnlockHeaders,
} from "../../utils/pinUnlockSession";

function DealDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { getVerifiedJWT } = useAuth();
  // Fetched user from /v1/pyr/user/:id — stored for the UI to be built out.
  // eslint-disable-next-line no-unused-vars
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // This page is pay data, so the read is refused without a live PIN unlock.
  // Don't even attempt it while locked — PinGate is showing the prompt.
  const fetchUser = useCallback(async () => {
    if (!id || !isPinUnlockSessionActive()) return;
    setLoading(true);
    setError(null);
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint(`/v1/pyr/user/${id}`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
          ...pinUnlockHeaders(),
        },
      });

      if (response.ok) {
        const result = await response.json();
        setUser(result?.data ?? result ?? null);
      } else {
        const body = await response.json().catch(() => ({}));
        if (isPinRequiredResponse(response, body)) {
          setUser(null);
          return;
        }
        setError("Unable to load this user.");
      }
    } catch (err) {
      setError("Something went wrong while loading this user.");
    } finally {
      setLoading(false);
    }
  }, [getVerifiedJWT, id]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  let content;
  if (loading) {
    content = (
      <Center py="xl">
        <Loader />
      </Center>
    );
  } else if (error) {
    content = (
      <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
        {error}
      </Alert>
    );
  } else {
    content = null;
  }

  return (
    <PageContent title="Deal Details">
      <Container size="xl" style={{ maxWidth: "100%", padding: "24px" }}>
        <Stack gap="lg">
          <Group justify="space-between" align="center">
            <Button
              variant="subtle"
              leftSection={<IconArrowLeft size={16} />}
              onClick={() => navigate(-1)}
            >
              Back
            </Button>
          </Group>

          <Divider />

          <PinGate
            description="Enter your 4-digit security PIN to view this user's pay."
            onUnlock={fetchUser}
          >
            {content}
          </PinGate>
        </Stack>
      </Container>
    </PageContent>
  );
}

export default DealDetailsPage;
